import { ViewsSyncRequestConfig, useStorytellClient } from "@storytell/client";
import { subscribeToGlobalEvent } from "@storytell/events";
import type { View } from "@storytell/types";
import { createResource, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";
import { createResourceStorage } from "~/lib/createResourceStorage";
import { createEntitySyncManager } from "../sync/createEntitySyncManager";

export type ViewsService = ReturnType<typeof createViewsService>;

export const createViewsService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
}) => {
  const client = useStorytellClient();

  const views = createEntitySyncManager<View & { userId: string }>(undefined, (view) => [view.name, view.description]);

  const storage = createResourceStorage(
    () => views.all(),
    (value) => views.hydrateFromSync(value || []),
  );

  const [viewsData, { refetch }] = createResource(
    () => !isServer && deps.authReady() && deps.userId(),
    async (): Promise<(View & { userId: string })[]> => {
      const userId = deps.userId();
      if (!userId) return [];
      const request = ViewsSyncRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      if (response.isErr()) {
        console.error("Failed to fetch views sync", response.error);
        return [];
      }
      const data = Object.values(response.value.views).map((view) => ({ ...view, userId }));
      if (isServer) {
        storage[1](data);
      }
      return data;
    },
    {
      initialValue: [] as (View & { userId: string })[],
      deferStream: true,
      storage: () => storage,
    },
  );

  onMount(() => {
    const unsubscribe = subscribeToGlobalEvent("user-context-changed", () => {
      refetch();
    });
    onCleanup(() => {
      unsubscribe();
    });
  });

  // This is used to trigger a suspense boundary so SSR works properly
  // Don't use it for data (that's why it's returned as undefined)
  const suspenseTrigger = () => (viewsData() ? undefined : undefined);

  return {
    suspenseTrigger,
    refetch,
    ...views,
  };
};
